import React from "react";
import styles from "./FHIRPatient.module.scss";
import _ from "lodash";
import DateTimePicker from "react-datetime-picker";
import { capitalize } from "../../utils/textFunctions";
import RecordOverrideOptions from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideOptions";
import { overrideSummaryForName } from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideUtil";
import { handleMultipleAttributeOverride } from "../../routes/NewSearchRecords/SearchRecordTable/MultipleAttributeOverride/Utils";
import { formattedDateWithDefault } from "../../utils/formattedDate";
import Modal from "../../components/ModalPopup";
import ButtonList from "../../components/ButtonList";
import Button from "../../components/Button";
import { isImdmClient } from "../../utils/isImdmClient";
import { useAuthState } from "../../components/AuthProvider";
import OverrideAuditInputModal from "../../routes/NewSearchRecords/SearchRecordTable/OverrideAuditInputModal";
import { renderFieldWithDiff } from "./Utils";

const PatientName = ({
  data,
  summary: isSummary,
  className,
  oncRecordOverrideIconClick,
  isInvisible = false,
  isAggregateInfo = false,
  isNewQuarantine = false,
  isMultipleAttributeOverride = false,
  setAttributeOverrides,
  attributeOverrides,
  hasDiff = false,
  diffInfo = null,
  isPreviewMode = false,
}) => {
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const isImdm = isImdmClient(selectedClient);
  const [replaceRecordOverrideDialog, setReplaceRecordOverrideDialog] =
    React.useState(false);
  const [recordOverrideDialogContent, setRecordOverrideDialogContent] =
    React.useState({});
  const [family, setFamily] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [middleName, setMiddleName] = React.useState("");
  const [valueChanged, setValueChanged] = React.useState(false);
  const [sourceTimeStamp, setSourceTimeStamp] = React.useState(new Date());
  const [suffixName, setSuffixName] = React.useState("");
  const [showAuditModal, setShowAuditModal] = React.useState(false);
  const [pendingConfirmDetails, setPendingConfirmDetails] =
    React.useState(null);

  if (isSummary && data.length === 0) {
    return <div>Name: [Empty]</div>;
  }

  if (isSummary) {
    const { family, given, use } = data[0];
    const nameDisplay = `${family ? family : ""}, ${given ? given.join(" ") : ""}`;

    // If in preview mode and there's a diff, show both prior and new values (only for use === "usual")
    if (isPreviewMode && hasDiff && diffInfo && use === "usual") {
      const priorName =
        diffInfo.prior && diffInfo.prior[0]
          ? `${diffInfo.prior[0].family || ""}, ${diffInfo.prior[0].given ? diffInfo.prior[0].given.join(" ") : ""}`
          : "";

      return (
        <div>
          Name:
          {diffInfo.changeType === "removed" ? (
            <span className="preview-diff-prior"> {priorName}</span>
          ) : diffInfo.changeType === "added" ? (
            <span className="preview-diff-highlight"> {nameDisplay}</span>
          ) : (
            <>
              <span className="preview-diff-prior"> {priorName}</span>
              {" → "}
              <span className="preview-diff-highlight"> {nameDisplay}</span>
            </>
          )}
        </div>
      );
    }

    return <div>Name: {nameDisplay}</div>;
  }

  if (!isSummary && data.length === 0) {
    return (
      <section>
        <h3>Names</h3>
        <div>
          <h4>Empty</h4>
        </div>
      </section>
    );
  }

  const prepareOverrideOriginalValue = (name, type) => {
    const extensionMap = overrideSummaryForName(name, type);
    oncRecordOverrideIconClick(extensionMap);
  };

  const actionHandler = (detailsMap) => {
    if (isMultipleAttributeOverride) {
      handleMultipleAttributeOverride(
        detailsMap,
        "name",
        attributeOverrides,
        setAttributeOverrides,
      );
      setReplaceRecordOverrideDialog(false);
    } else {
      oncRecordOverrideIconClick(detailsMap);
    }
  };

  const handleReplaceOverride = (detailsMap) => {
    if (detailsMap["Mark as"] && detailsMap["Mark as"] === "Replace") {
      if (detailsMap.originalFieldData) {
        setFamily(detailsMap.originalFieldData.family);
        setSuffixName(
          detailsMap.originalFieldData.suffix
            ? detailsMap.originalFieldData.suffix.join(",")
            : "",
        );
        setFirstName(
          !detailsMap.originalFieldData.given
            ? ""
            : detailsMap.originalFieldData.given.length > 1
              ? detailsMap.originalFieldData.given.slice(0, -1).join(",")
              : detailsMap.originalFieldData.given[0],
        );
        setMiddleName(
          !detailsMap.originalFieldData.given
            ? ""
            : detailsMap.originalFieldData.given.length > 1
              ? detailsMap.originalFieldData.given.slice(-1).toString()
              : "",
        );
      }
      setSourceTimeStamp(
        detailsMap["Source Timestamp"] && detailsMap["Source Timestamp"] !== "-"
          ? new Date(detailsMap["Source Timestamp"])
          : new Date(),
      );
      setRecordOverrideDialogContent(detailsMap);
      setReplaceRecordOverrideDialog(true);
    } else {
      actionHandler(detailsMap);
    }
  };

  const handleConfirm = () => {
    const detailsMap = { ...recordOverrideDialogContent };

    // Family Name
    detailsMap.originalFieldData.family = family;

    // Given Name
    detailsMap.originalFieldData.given = !_.isEmpty(middleName)
      ? isImdm
        ? [firstName, middleName]
        : firstName.split(",").concat([middleName])
      : isImdm
        ? [firstName]
        : firstName.split(",");

    // Suffix
    if (suffixName) {
      detailsMap.originalFieldData.suffix = suffixName.split(",");
    } else {
      delete detailsMap.originalFieldData.suffix;
    }

    // Period
    if (!detailsMap.originalFieldData.period)
      detailsMap.originalFieldData.period = {};
    detailsMap.originalFieldData.period.start = sourceTimeStamp.toISOString();

    if (isMultipleAttributeOverride)
      // Directly proceed to next step without audit modal
      actionHandler(detailsMap);
    else {
      setPendingConfirmDetails(detailsMap);
      setShowAuditModal(true);
      // Do NOT call actionHandler(detailsMap) directly here
    }
  };

  const handleAuditSubmit = (auditInfo) => {
    if (pendingConfirmDetails) {
      const updatedDetails = {
        ...pendingConfirmDetails,
        auditInfoList: auditInfo,
      };
      actionHandler(updatedDetails);
      setPendingConfirmDetails(null);
    }
    setShowAuditModal(false);
  };

  const replaceOverrideModal = () => {
    return (
      <Modal
        open={replaceRecordOverrideDialog}
        onClose={() => setReplaceRecordOverrideDialog(false)}
      >
        <h3 style={{ textAlign: "center" }}>Replace override</h3>
        <br />
        <br />
        <div>
          {recordOverrideDialogContent && (
            <table className={"recOvrTable"}>
              <tr>
                <td>Last Name</td>
                <td>Suffix</td>
                <td>First Name*</td>
                <td>Middle Name**</td>
                <td>Use</td>
                <td>Attribute</td>
                <td>Source Timestamp</td>
              </tr>
              <tr>
                <td>
                  <input
                    type="text"
                    value={family}
                    onChange={(e) => {
                      setFamily(e.target.value);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={suffixName}
                    onChange={(e) => {
                      setSuffixName(e.target.value);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                  />
                </td>

                <td>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={middleName}
                    onChange={(e) => {
                      setMiddleName(e.target.value);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={recordOverrideDialogContent["Use"]}
                    disabled={true}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={recordOverrideDialogContent["Attribute"]}
                    disabled={true}
                  />
                </td>
                <td>
                  <DateTimePicker
                    format="MM-dd-y hh:mm:ss a"
                    value={sourceTimeStamp}
                    onChange={(changeDate) => {
                      setSourceTimeStamp(changeDate);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                  />
                </td>
              </tr>
            </table>
          )}
        </div>
        <small>
          *Separate multiple First Names with a comma. For example, the names
          "Anne", "Anne Marie", and "Marie" can be entered as: "Anne,Anne
          Marie,Marie".
        </small>
        <br />
        <small>
          **A Middle Name cannot be saved without a First Name present.
        </small>
        <br />
        <br />
        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              primary
              disabled={!valueChanged || (middleName && !firstName)}
              onClick={() => {
                handleConfirm();
              }}
            >
              Confirm Changes
            </Button>
            <Button
              onClick={() => {
                setValueChanged(false);
                setReplaceRecordOverrideDialog(false);
              }}
            >
              Cancel
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  return (
    <section>
      <div className={className + " " + styles.header}>
        {!isAggregateInfo && (
          <div
            className={
              isMultipleAttributeOverride
                ? styles.multipleAttributeOverrideColumn
                : styles.actionColumn
            }
          />
        )}
        <div>
          <h4>Last Name</h4>
        </div>
        <div>
          <h4>Suffix</h4>
        </div>
        <div>
          <h4>First Name</h4>
        </div>
        <div>
          <h4>Middle Name</h4>
        </div>
        <div>
          <h4>Use</h4>
        </div>
        <div>
          <h4>Attribute</h4>
        </div>
        <div>
          <h4>Source Timestamp</h4>
        </div>
        {isAggregateInfo && (
          <div>
            <h4>Source</h4>
          </div>
        )}
      </div>

      {data.map((name, index) => {
        const attrCode = name.extension
          ? name.extension.filter((e) => e.url.includes("attrCode"))
          : "-";
        const sourceCode = name.extension
          ? name.extension.filter((e) => e.url.includes("sourceCode"))
          : "-";

        // Get prior name data if available for diff comparison (only for use === "usual")
        const isUsualName = name.use === "usual";
        const priorName =
          isPreviewMode &&
          hasDiff &&
          diffInfo &&
          diffInfo.prior &&
          diffInfo.prior[index] &&
          isUsualName
            ? diffInfo.prior[index]
            : null;
        const changeType = diffInfo && isUsualName ? diffInfo.changeType : null;

        const patientMap = {
          Field: "Name",
          "Family Name": name.family ? name.family : "-",
          "Given Name 1": !name.given
            ? "-"
            : name.given.length > 1
              ? name.given.slice(0, -1).join(", ")
              : name.given,
          "Given Name 2": !name.given
            ? "-"
            : name.given.length > 1
              ? name.given.slice(-1)
              : "-",
          Suffix: name.suffix ? name.suffix : "-",
          Use: name.use ? capitalize(name.use) : "-",
          Attribute: attrCode[0] ? attrCode[0].valueString : "-",
          Source: sourceCode[0] ? sourceCode[0].valueString : "-",
          "Source Timestamp":
            name.period && name.period.start
              ? formattedDateWithDefault(name.period.start)
              : "-",
          originalFieldName: "name",
          originalFieldData: name,
          originalFieldDataCopy: JSON.parse(JSON.stringify(name)),
          id: index,
        };

        // Prepare prior values for diff display
        const priorPatientMap = priorName
          ? {
              "Family Name": priorName.family ? priorName.family : "-",
              "Given Name 1": !priorName.given
                ? "-"
                : priorName.given.length > 1
                  ? priorName.given.slice(0, -1).join(", ")
                  : priorName.given,
              "Given Name 2": !priorName.given
                ? "-"
                : priorName.given.length > 1
                  ? priorName.given.slice(-1)
                  : "-",
              Suffix: priorName.suffix
                ? priorName.suffix.map((suffixName, sKey) => {
                    return <div key={sKey}>{suffixName}</div>;
                  })
                : "-",
              Use: priorName.use ? capitalize(priorName.use) : "-",
            }
          : null;

        return (
          <div
            key={index}
            className={`${className} ${hasDiff && isPreviewMode && isUsualName ? "preview-diff-highlight" : ""}`}
          >
            {!isAggregateInfo && (
              <div
                className={
                  isMultipleAttributeOverride
                    ? styles.multipleAttributeOverrideColumn
                    : styles.actionColumn
                }
                style={{ zIndex: 998 - index }}
              >
                {oncRecordOverrideIconClick &&
                  !isInvisible &&
                  !isNewQuarantine && (
                    <RecordOverrideOptions
                      detailsMap={patientMap}
                      onClickRecordOverrideAction={(detailsMap) =>
                        handleReplaceOverride(detailsMap)
                      }
                      onClickRecordOverrideButton={(type) =>
                        prepareOverrideOriginalValue(
                          patientMap.originalFieldData,
                          type,
                        )
                      }
                      extension={
                        name.hasOwnProperty("extension")
                          ? name.extension.filter(
                              (item) =>
                                item.url ===
                                "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
                            )
                          : []
                      }
                      isMultipleAttributeOverride={isMultipleAttributeOverride}
                      selectedOverrides={attributeOverrides.name}
                    />
                  )}
              </div>
            )}
            <div>
              {renderFieldWithDiff(
                patientMap["Family Name"],
                priorPatientMap ? priorPatientMap["Family Name"] : null,
                changeType,
                isPreviewMode,
                hasDiff,
              )}
            </div>
            <div>
              {renderFieldWithDiff(
                patientMap["Suffix"],
                priorPatientMap ? priorPatientMap["Suffix"] : null,
                changeType,
                isPreviewMode,
                hasDiff,
              )}
            </div>
            <div>
              {renderFieldWithDiff(
                patientMap["Given Name 1"],
                priorPatientMap ? priorPatientMap["Given Name 1"] : null,
                changeType,
                isPreviewMode,
                hasDiff,
              )}
            </div>
            <div>
              {renderFieldWithDiff(
                patientMap["Given Name 2"],
                priorPatientMap ? priorPatientMap["Given Name 2"] : null,
                changeType,
                isPreviewMode,
                hasDiff,
              )}
            </div>
            <div>
              {renderFieldWithDiff(
                patientMap["Use"],
                priorPatientMap ? priorPatientMap["Use"] : null,
                changeType,
                isPreviewMode,
                hasDiff,
              )}
            </div>
            <div>
              <div>{patientMap["Attribute"]}</div>
            </div>
            <div>
              <div>{patientMap["Source Timestamp"]}</div>
            </div>
            {isAggregateInfo && (
              <div>
                <div>{patientMap["Source"]}</div>
              </div>
            )}
          </div>
        );
      })}
      {replaceRecordOverrideDialog && replaceOverrideModal()}
      {showAuditModal && (
        <OverrideAuditInputModal
          isOpen={showAuditModal}
          onCancel={() => setShowAuditModal(false)}
          onSubmit={handleAuditSubmit}
        />
      )}
    </section>
  );
};

export default PatientName;
